/**
 * @file BuilderWorkspace.tsx
 * @description Рабочая область конструктора на планшете и компьютере
 * (phase19.16 Р1-6а; uiux-builder-shell.md §2, §9 пороги; §7.16 фазы):
 * от 1280 — `WorkspaceDesktop` (навигатор · канва · панель), 768–1279 —
 * `WorkspaceTablet` (навигатор или панель слева, канва справа).
 *
 * Боковые колонки тянутся ручками и сворачиваются в полоску (партия после
 * приёмки Р2а): ширины, свёртка и память — `useBuilderColumnWidths`.
 * Свёрнутая панель настроек разворачивается сама, когда выбирают блок,
 * элемент, шапку/подвал или раздел (иначе щёлкнули — и ничего не видно);
 * навигатор от 1280 — только кнопкой. На телефоне свой экран, ручек нет.
 * Форма блока живёт в самой панели (`useSectionDraft` в `SectionBlockEditor`)
 * и пропадает вместе со свёрнутой панелью, поэтому перед свёрткой колонки
 * с панелью спрашиваем `leave` (то же окно «Блок не сохранён», что у «←»).
 * С 1024 область закреплена от шапки до низа окна (`workspaceAreaClass`,
 * замечание владельца 2026-10-18), высота через `dvh` — только до 1024.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { useBuilder } from "./builder-context";
import type { ColumnsApi } from "./workspace/column-controls";
import { WorkspaceDesktop } from "./workspace/WorkspaceDesktop";
import { WorkspaceTablet } from "./workspace/WorkspaceTablet";
import { useBuilderColumnWidths } from "../../hooks/site-builder/useBuilderColumnWidths";
import type { BuilderColumnKey } from "../../lib/site-builder/column-widths";
import { BUILDER_PANEL_META } from "../../lib/site-builder/panels";
import { workspaceAreaClass } from "../../lib/site-builder/workspace-area";


export function BuilderWorkspace() {
  const b = useBuilder();
  const { selection } = b;
  const panel = selection.panel && selection.panel !== "pages" ? selection.panel : null;
  const spread = panel !== null && BUILDER_PANEL_META[panel].wide;
  const desktop = b.layout === "desktop";
  const widths = useBuilderColumnWidths(desktop ? "desktop" : "tablet", desktop && !spread);
  const sel = selection.sel;
  const selId = sel.kind === "block" ? sel.id : sel.kind === "area" ? sel.area : "";
  const selectionKey = `${selection.panel ?? ""}|${sel.kind}|${selId}|${b.element?.elementId ?? ""}`;
  const choosing = selection.panel !== null || sel.kind !== "page";

  // Планшет: слева навигатор, пока ничего не выбрано; выбор открывает панель.
  const [leftPanel, setLeftPanel] = useState(false);
  useEffect(() => {
    setLeftPanel(choosing);
  }, [selectionKey, choosing]);

  // Свёрнутая колонка с панелью разворачивается при новом выборе (не при входе).
  const prevKey = useRef(selectionKey);
  const { columns, expand, collapse: fold } = widths;
  const panelCollapsed = columns.panel?.collapsed === true;
  const sideCollapsed = columns.side?.collapsed === true;
  useEffect(() => {
    if (prevKey.current === selectionKey) return;
    prevKey.current = selectionKey;
    if (desktop) {
      if (panelCollapsed) expand("panel");
    } else if (choosing && sideCollapsed) expand("side");
  }, [selectionKey, desktop, choosing, panelCollapsed, sideCollapsed, expand]);

  // Фокус после свёртки — на «Развернуть» той же колонки. Полоска берёт фокус в своём
  // эффекте (дочерние — раньше), затем ключ сбрасывается: иначе полоска, заново
  // смонтированная при смене ширины окна, снова забрала бы фокус (`autoFocus`).
  const [focusKey, setFocusKey] = useState<BuilderColumnKey | null>(null);
  useEffect(() => {
    if (focusKey !== null) setFocusKey(null);
  }, [focusKey]);
  const { leave } = b;
  const collapse = useCallback(
    (key: BuilderColumnKey) => {
      const doFold = () => {
        setFocusKey(key);
        fold(key);
      };
      // В колонке панель настроек — несохранённый блок спрашиваем; «Остаться» — не сворачиваем.
      const holdsPanel = key === "panel" || (key === "side" && leftPanel);
      if (!holdsPanel) return doFold();
      void leave().then((ok) => {
        if (ok) doFold();
      });
    },
    [fold, leave, leftPanel],
  );
  const api: ColumnsApi = { widths, collapse, focusKey };

  if (desktop) return <WorkspaceDesktop api={api} spread={spread} heightClass={workspaceAreaClass("desktop")} />;
  return <WorkspaceTablet api={api} leftPanel={leftPanel} onLeftPanel={setLeftPanel} heightClass={workspaceAreaClass("tablet")} />;
}
